import React, { useState } from "react";

import ReactMarkdown from "react-markdown";
import { MdArrowDropDown } from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";
import { TiTickOutline } from "react-icons/ti";

const data = [
  {
    name: "Strategic Business Transformation",
    text: `You're constantly facing unknowns, challenges and untapped opportunities. We guide you forward to safely innovate and transform.
## 1. **Evolve strategy & business plans**
Align your organisation’s vision, goals, and actions, even in highly charged situations. We help you consider factors and risks, develop measures for transformation and steps for success.

## 2. **Improve management performance**
More confidently make decisions that combine smart technology with hindsight, insight and foresight. Better execute operationally and proactively manage emerging risks, complexity and scale.  

## 3. **Enable enterprise governance**
Better control, direct or regulate organisational performance.  Working collaboratively, we provide evidence-based advice – enabling you to improve governance and effectively meet legal, strategic, community and environmental obligations.

## 4. **Hone enterprise risk management**
Ensure your enterprise risk management activities support executive decision-making. Understand risk appetite and biases affecting your culture; identify and track critical controls; and build better risk processes. Gain meaningful insights through business simulation, testing, and analytics.`,
  },
  {
    name: "Preparing for implementation",
    text: `Transformational success relies strongly on building good foundations and structures that sustain it over time.
## **1. Framework for benefits realisation**
Ensure you can prioritise and identify the right investments through our benefits management framework.

## **2. Process design**
Improve effectiveness through rigorously designed processes aligned to your outcomes. This includes equipping staff with guidelines, documentation and training on customer interactions and daily operations.
    
## **3. People impact**
The best technical solutions and most efficient business processes will still fail if not supported by a prepared and engaged workforce. We design, implement and support effective change measures, ensuring successful adoption, driving value realisation and return on investment.

## **4. Holistic approach**
Align your messaging and context across communications, engagement, and training. All our interventions integrate to deliver a clear story that resonates with your people to deliver high levels of adoption—helping you meet project and business objectives.`,
  },
  {
    name: "Realise and Sustain benefits",
    text: `Enable successful delivery and uptake of your transformation projects for long-term benefit.

## 1. **Deploy results-driven change**
 To effectively implement organisation-wide transformation, you want change partners with a proven methodology. Our change approach is informed by the industry recognised Prosci ADKAR™ model, enhanced to meet the real requirements of organisations in ANZ.
    
## 2. **Drive successful outcomes today**
Overall project success depends on high levels of adoption. We manage the people element of your transformation, working as an equal partner with your technical and process teams. As the voice of your users, we provide checks and balances to ensure business and technical readiness, so your objectives are met.
    
## **3. Prepare for your future success**
You can’t predict the future, but you can improve your ability to anticipate change. We help you bridge the gap between guesswork and readiness. Collaborating with you and futurists, facilitators and designers, we help you better understand possible opportunities and threats.`,
  },
];

const ServiceHighlights = () => {
  const [selectedItem, setSelectedItem] = useState(data[0]);
  const [showDropdown, setShowDropdown] = useState(false);
  const handleItemClick = (item) => {
    setSelectedItem(item);
    setShowDropdown(false); // Close dropdown after selection
  };
  const dropdownVariants = {
    hidden: { scaleY: 0, opacity: 0, originY: 0 },
    visible: { scaleY: 1, opacity: 1, originY: 0 },
  };
  return (
    <div
      id="service-highlights"
      className="flex flex-col py-[50px] dark:bg-dark gap-[70px] items-center justify-center"
    >
      <div className="relative text-center">
        <h1 className="text-4xl font-extrabold md:text-7xl font-mono text-center dark:text-light text-[#4f1951] leading-normal">
          Service Highlights
        </h1>
        <div
          className="dark:bg-custom-gradient"
          style={{
            position: "absolute",
            width: "100%",
            height: "157.05px",
            left: "50%", // Center horizontally
            top: "50%", // Center vertically
            transform: "translate(-50%, -50%) matrix(1, 0, -0.03, 1, 0, 0)", // Adjust for precise positioning
            opacity: 0.2,
            filter: "blur(34px)",
            borderRadius: "50%",
            zIndex: "0", // Ensure it's behind the text
          }}
        ></div>
      </div>

      <p className="text-dark dark:text-white  text-lg font-bold font-serif text-center w-full sm:w-[60%] lg:w-[50%] xl:w-[30%]">
        In a world full of noise, we’re an experienced and independent voice you
        can trust
      </p>

      <div className="flex mx-auto w-full px-3 lg:px-0 lg:w-[80%] flex-col lg:flex-row gap-10 items-center lg:items-start justify-center">
        {/* Dropdown for medium and smaller screens */}
        <div className="lg:hidden w-[90%]  sm:w-[60%]">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="text-white flex flex-row items-center justify-between font-bold py-2 rounded-lg bg-[#4f1951] dark:bg-dark dark:text-light dark:border-light dark:border-[1px]  px-4 w-full"
          >
            <p>{selectedItem.name}</p>
            <MdArrowDropDown />
          </button>
          <AnimatePresence>
            {showDropdown && (
              <motion.div
                className={`flex flex-col w-full  px-3 dark:bg-dark dark:text-white dark:border-white dark:border-[1px] bg-[#4f1951] rounded-xl mt-2`}
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={dropdownVariants}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {data.map((item, index) => (
                  <button
                    key={index}
                    className={`flex flex-row justify-between items-center  text-white py-3 border-b-[1px] border-b-white  duration-300 font-bold`}
                    onClick={() => handleItemClick(item)}
                  >
                    <p>{item.name}</p>
                    {selectedItem.name === item.name && <TiTickOutline />}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="hidden lg:flex flex-col gap-5 w-[40%]">
          {data.map((item, index) => (
            <button
              key={index}
              className={`text-center  hover:bg-[#4f1951] dark:hover:text-dark dark:hover:bg-light  hover:text-white hover:py-3 hover:rounded-lg  duration-300 hover:scale-105 font-bold my-2 ${
                selectedItem === item
                  ? "text-white bg-[#4f1951] dark:bg-white dark:text-dark py-3 rounded-xl "
                  : "text-dark dark:text-light"
              }`}
              onClick={() => handleItemClick(item)}
            >
              {item.name}
            </button>
          ))}
        </div>
        <div className="flex flex-col w-[90%] shadow-md  shadow-dark lg:w-[60%] dark:bg-dark dark:text-light dark:shadow-light bg-[#f2f2f2] p-10 rounded-xl font-sans">
          <ReactMarkdown className="whitespace-pre-wrap">
            {selectedItem.text}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default ServiceHighlights;
